/**
 * PanchangaCard — the Daily Panchanga reference surface (editorial redesign).
 *
 * Layout (see docs/DAILY_PANCHANGA_UI_SPEC.md §8):
 *   1. Context band — 5 columns (Saṃvatsara · Ayana · Ṛtu · Māsa · Pakṣa), each
 *      label + Fraunces value. Māsa shows BOTH frames (amānta + pūrṇimānta).
 *   2. Sun/moon times strip (below the band, above the table).
 *   3. Five-aṅga table (Tithi, Vāra, Nakṣatra, Yoga, Karaṇa): label | Fraunces value
 *      (+ muted timing / "then …") | per-segment badges (tithi type + Lord; Vāra Lord;
 *      Nakṣatra Deity + Lord). Yoga / Karaṇa carry no side badges.
 *   4. Nakṣatra meaning footer (symbol + description), action buttons.
 *
 * The page header ("Dina Pañcāṅga" + how-to link) is rendered full-width by
 * DailyPanchangaView, not here. All colors bind to the `.kg-warm` token set.
 */

import React from 'react';
import { Badge } from '../ui/badge';
import { SanskritTerm, SANSKRIT_EXPLANATIONS } from '../SanskritTerm/SanskritTerm';
import { MoonPhase } from '../ui/MoonPhase';
import { isWaxingPaksha } from '../../lib/paksha';
import { Sunrise, Sunset, Moon, Share2, Copy, ExternalLink, Flower2, Sun, CloudRain, Leaf, Snowflake, Cloud } from 'lucide-react';
import type { PanchangaResponse, Locale } from '../../types/api';
import type { LocationData } from '../LocationSearch/LocationSearch';
import { uiString } from '../../i18n/uiStrings';
import tithiPropertiesData from '../../data/tithi_properties.json';
import nakshatraPropertiesData from '../../data/nakshatra_properties.json';
import { angaRows, AngaKind, asciiDigits } from './angaRows';

interface PanchangaCardProps {
  panchanga: PanchangaResponse;
  selectedLocation?: LocationData | null;
  locale?: Locale;
  onViewAlignment?: () => void;
  onNavigateToArticle?: (articleId: string) => void;
}

export const PanchangaCard: React.FC<PanchangaCardProps> = ({ panchanga, locale = 'en', onViewAlignment }) => {
  const L = (key: Parameters<typeof uiString>[1]) => uiString(locale, key);
  // Normalize to NFC so precomposed diacritics (ā, ī, ṇ) render with correctly
  // positioned marks (some API fields arrive NFD). See DAILY_PANCHANGA_UI_SPEC §8.
  const nfc = (s: string): string => (s || '').normalize('NFC');

  /** One displayed aṅga segment, from `angas` (number set) or the legacy sentence (raw). */
  type Seg = { name: string; timing: string | null; open: boolean; number: number | null; raw: string };

  const getTithiProperties = (num: number | null) => {
    if (!num || num < 1 || num > 30) return null;
    return tithiPropertiesData.tithi_properties[num.toString() as keyof typeof tithiPropertiesData.tithi_properties] || null;
  };

  const getPlanetSanskrit = (planet: string): string => {
    const map: Record<string, string> = {
      Sun: 'Sūrya', Moon: 'Candra', Mars: 'Kuja', Mercury: 'Budha',
      Jupiter: 'Guru', Venus: 'Śukra', Saturn: 'Śani', Rahu: 'Rāhu', Ketu: 'Ketu',
    };
    return map[planet] || planet;
  };

  const getVaaraLord = (vaara: string): string | null => {
    if (!vaara) return null;
    const v = vaara.toLowerCase();
    if (/bhānuvāra|ravivāra|sunday/.test(v)) return 'Sūrya';
    if (/somavāra|monday/.test(v)) return 'Candra';
    if (/maṅgalavāra|mangalavara|tuesday/.test(v)) return 'Kuja';
    if (/budhavāra|wednesday/.test(v)) return 'Budha';
    if (/guruvāra|bṛhaspativāra|thursday/.test(v)) return 'Guru';
    if (/śukravāra|shukravara|friday/.test(v)) return 'Śukra';
    if (/śanivāra|shanivara|saturday/.test(v)) return 'Śani';
    return null;
  };

  // By engine number (1-27), the key of nakshatra_properties.json; names agree with
  // the API's for all 27 (B-139-WEB). Used for rows built from `angas`.
  const nakshatraPropsByNumber = (num: number): any =>
    (nakshatraPropertiesData.nakshatra_properties as any)[String(num)] || null;

  // ── Per-segment side badges ──────────────────────────────────────────
  // Badges are secondary metadata, so they use a muted warm tone (--kg-meta),
  // NOT the indigo accent — indigo is reserved for interactive links. Tithi /
  // Vāra / Nakṣatra chips + the Homa deity names all share this same quiet tone.
  const badgeCls = 'text-xs px-1.5 py-0.5 font-medium border rounded';
  const badgeStyle: React.CSSProperties = {
    color: 'var(--kg-meta)',
    borderColor: 'var(--kg-meta-border)',
    background: 'transparent',
  };
  const tithiBadges = (seg: Seg): React.ReactNode => {
    const props = getTithiProperties(seg.number);
    if (!props) return null;
    return (
      <>
        <Badge variant="outline" className={badgeCls} style={badgeStyle}>{props.type}</Badge>
        <Badge variant="outline" className={badgeCls} style={badgeStyle}>Lord: {props.lord_sanskrit}</Badge>
      </>
    );
  };
  const vaaraBadges = (vaara: string): React.ReactNode => {
    const lord = getVaaraLord(vaara);
    return lord ? (
      <Badge variant="outline" className={badgeCls} style={badgeStyle}>Lord: {lord}</Badge>
    ) : null;
  };
  const nakshatraBadges = (seg: Seg): React.ReactNode => {
    const props = seg.number != null ? nakshatraPropsByNumber(seg.number) : null;
    const lord = props?.ruling_planet ? getPlanetSanskrit(props.ruling_planet) : null;
    if (!props?.deity && !lord) return null;
    return (
      <>
        {props?.deity && <Badge variant="outline" className={badgeCls} style={badgeStyle}>Deity: {nfc(props.deity)}</Badge>}
        {lord && <Badge variant="outline" className={badgeCls} style={badgeStyle}>Lord: {lord}</Badge>}
      </>
    );
  };

  // ── Derived data ─────────────────────────────────────────────────────
  // Structured māsa from the API (B-139-MASA); the sentence parse is only a fallback
  // for responses without masa_info, removed in the contract step (B-139-CON).
  const masaFromInfo = (() => {
    const info = panchanga.masa_info;
    if (!info) return null;
    const adhika = info.is_adhika ? ` (${L('adhika')})` : '';
    return { amaanta: info.amanta.name + adhika, purnimaanta: info.purnimanta.name + adhika };
  })();
  // Without masa_info the sentence is shown whole, never parsed (B-139-CON).
  const masaInfo = masaFromInfo ?? (panchanga.masa ? { amaanta: panchanga.masa, purnimaanta: null } : null);
  const masaAmaanta = masaInfo?.amaanta || null;
  const masaPurnimanta = masaInfo?.purnimaanta || null;

  // Rows from the structured `angas` (B-139-WEB). Without them (fail-soft null) the
  // server's sentence is shown whole, never parsed (B-139-CON).
  // Times use 0-9 in every language, Sanskrit included (author, 2026-10-12).
  const tillText = (time: string) => (locale === 'en' ? `${L('till')} ${time}` : `${time} ${L('till')}`);
  const segsFor = (kind: AngaKind, sentence: string | undefined): Seg[] => {
    const structured = panchanga.angas?.[kind];
    if (structured && structured.length > 0) {
      return angaRows(structured, kind, panchanga.date).map(r => ({
        name: nfc(r.name),
        timing: r.time ? tillText(r.time) : null,
        open: r.time == null,
        number: r.number,
        raw: r.nameEn,
      }));
    }
    return sentence && sentence !== '--'
      ? [{ name: nfc(sentence.trim()), timing: null, open: false, number: null, raw: sentence }]
      : [];
  };
  const tithiSegments = segsFor('tithi', panchanga.tithi);
  const nakshatraSegments = segsFor('nakshatra', panchanga.nakshatra);
  const yogaSegments = segsFor('yoga', panchanga.yoga);
  const karanaSegments = segsFor('karana', panchanga.karana);
  const tithiNumber = tithiSegments[0]?.number ?? null;

  // Moon phase beside Tithi (tithi IS the lunar day, so the phase belongs here).
  // Prefer the exact illuminated fraction from the API; fall back to a tithi-derived
  // estimate so the glyph still renders on older/cached responses.
  // NFD-robust pakṣa detection — the API sends "Śukla"/"Kṛṣṇa" decomposed, which
  // a precomposed regex silently misses (flipping the lit limb). See lib/paksha.
  const isWaxingMoon = panchanga.paksha
    ? isWaxingPaksha(panchanga.paksha)
    : (tithiNumber != null ? tithiNumber <= 15 : true);
  const tithiDerivedIllum: number | null = (() => {
    if (tithiNumber == null) return null;
    if (tithiNumber === 15) return 100;
    if (tithiNumber === 30) return 0;
    if (tithiNumber >= 16 && tithiNumber <= 29) return 100 - ((tithiNumber - 15) / 15) * 100;
    if (tithiNumber >= 1 && tithiNumber <= 14) return (tithiNumber / 15) * 100;
    return null;
  })();
  const moonIllum: number | null = panchanga.moon_illumination != null
    ? panchanga.moon_illumination
    : tithiDerivedIllum;
  // Absolute moon tones (NOT theme tokens): the lit limb must read bright and the
  // shadow genuinely dark on the light paper, so a crescent looks like a crescent
  // (mostly shadow) rather than inverting into a mostly-pale disc.
  const MOON_LIT = '#ECDCA6';   // moonlight
  const MOON_DARK = '#4A4235';  // shadow — visible on both light paper and dark navy
  const MOON_STROKE = '#8C7C57';

  // Season icon (Ṛtu) + phase disc (Pakṣa) for the band.
  const rituIcon = panchanga.rithu ? (() => {
    const r = panchanga.rithu.toLowerCase();
    const cls = 'h-[17px] w-[17px] shrink-0';
    // Match IAST/English AND the native-script forms (sa/hi Devanāgarī, te, kn),
    // since the value arrives localized — otherwise every non-English ritu fell
    // through to the generic default icon.
    if (/vasanta|spring|वसंत|వసంత|ವಸಂತ/.test(r)) return <Flower2 className={cls} style={{ color: '#059669' }} aria-label="Spring" />;
    if (/grīṣma|grishma|summer|ग्रीष्म|గ్రీష్మ|ಗ್ರೀಷ್ಮ/.test(r)) return <Sun className={cls} style={{ color: '#ca8a04' }} aria-label="Summer" />;
    if (/varṣā|varsha|rainy|monsoon|वर्षा|వర్ష|ವರ್ಷ/.test(r)) return <CloudRain className={cls} style={{ color: '#2563eb' }} aria-label="Rainy" />;
    if (/śarad|sharad|autumn|शरद|శరద|ಶರದ/.test(r)) return <Leaf className={cls} style={{ color: '#ea580c' }} aria-label="Autumn" />;
    if (/hemanta|हेमंत|హేమంత|ಹೇಮಂತ/.test(r)) return <Snowflake className={cls} style={{ color: '#0891b2' }} aria-label="Early winter" />;
    if (/śiśira|sisira|शिशिर|శిశిర|ಶಿಶಿರ/.test(r)) return <Snowflake className={cls} style={{ color: '#64748b' }} aria-label="Late winter" />;
    return <Cloud className={cls} style={{ color: '#059669' }} aria-label="Season" />;
  })() : null;

  const pakshaIcon = panchanga.paksha ? (() => {
    const p = panchanga.paksha.toLowerCase();
    const isPhase = /kṛṣṇa|krishna|krsna|waning|śukla|shukla|sukla|waxing/.test(p);
    if (isPhase && moonIllum != null) {
      return (
        <MoonPhase
          illumination={moonIllum}
          waxing={isWaxingMoon}
          size={15}
          litColor={MOON_LIT}
          darkColor={MOON_DARK}
          strokeColor={MOON_STROKE}
          className="shrink-0 align-middle"
        />
      );
    }
    return <Moon className="h-[15px] w-[15px] shrink-0" style={{ color: 'var(--kg-sub)' }} />;
  })() : null;

  type BandCell =
    | { label: string; value: string; icon?: React.ReactNode }
    | { label: string; masa: { amaanta: string; purnimaanta: string | null } };

  const bandCells: BandCell[] = [
    panchanga.samvatsara && { label: L('samvatsara'), value: nfc(panchanga.samvatsara) },
    panchanga.ayana && { label: L('ayana'), value: nfc(panchanga.ayana) },
    panchanga.rithu && { label: L('ritu'), value: nfc(panchanga.rithu), icon: rituIcon },
    masaAmaanta && { label: L('masa'), masa: { amaanta: nfc(masaAmaanta), purnimaanta: masaPurnimanta ? nfc(masaPurnimanta) : null } },
    panchanga.paksha && { label: L('paksha'), value: nfc(panchanga.paksha), icon: pakshaIcon },
  ].filter(Boolean) as BandCell[];

  // The footer is English prose, so it is shown on the English page only (author's
  // review of B-139-WEB).
  const nakshatraFooterLines = (locale === 'en' ? nakshatraSegments : []).map(seg => {
    const props = seg.number != null ? nakshatraPropsByNumber(seg.number) : null;
    const standsFor = (props?.stands_for || '').toLowerCase();
    const desc = props?.description
      || (standsFor ? `${props.name} is about ${standsFor.replace(/^star of /, '')}.` : null);
    return {
      name: seg.name,
      standsFor: props?.stands_for ? nfc(props.stands_for) : null,
      symbol: props?.symbol ? nfc(props.symbol) : null,
      desc,
    };
  }).filter(line => line.standsFor || line.desc);

  const hasLimbs = tithiSegments.length > 0 || nakshatraSegments.length > 0 || yogaSegments.length > 0
    || karanaSegments.length > 0 || !!(panchanga.vaara && panchanga.vaara !== '--');

  // ── Row renderer: label | (segment value + timing) ↔ badges per line ──
  // Muted timing text ("till 22:18"), with a past-midnight tooltip for >24:00.
  const timingSpan = (t: string | null) => {
    if (!t) return null;
    const isPastMidnight = /\b(2[4-9]|[3-9]\d):/.test(t);
    return (
      <span
        className="ml-1.5 text-[12.5px]"
        style={{ color: 'var(--kg-sub)' }}
        title={isPastMidnight ? 'past midnight (24:00 = midnight)' : undefined}
      >
        {t}
      </span>
    );
  };

  // A following segment's name. Outside English the open successor carries the
  // trailing connective ("తృతీయ తర్వాత"), as the server sentence does; English leads
  // with "then " instead. Legacy rows already contain it in their text.
  const followName = (seg: Seg): string =>
    seg.open && seg.number != null && L('afterwards') ? `${seg.name} ${L('afterwards')}` : seg.name;

  const renderLimb = (
    key: string,
    label: React.ReactNode,
    lines: Seg[],
    badgesFor?: (seg: Seg) => React.ReactNode,
    firstTimingFallback?: string,
    accessory?: React.ReactNode,
  ) => {
    const first = lines[0];
    const tail = lines.slice(1);
    // A lone subsequent segment keeps its own row so its side badges stay aligned;
    // a longer tail (only Karaṇa reaches 3-4) collapses onto one comma-joined line.
    const mergeTail = tail.length > 1;
    const firstParts = first ?? null;

    return (
      <div key={key} className="grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-4 py-3.5 sm:grid-cols-[6rem_minmax(0,1fr)]">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: 'var(--kg-faint)' }}>
          {label}
        </div>
        <div className="min-w-0 space-y-1">
          {/* Row 0 — current segment in Fraunces, with its side badges. */}
          {firstParts && (
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <span className="min-w-0">
                <span className="font-fraunces text-[19px] font-medium leading-tight" style={{ color: 'var(--kg-ink)' }}>{firstParts.name}</span>
                {timingSpan(firstParts.timing || firstTimingFallback || null)}
                {/* Trailing comma signals the sequence continues on the "then …" line
                    below (e.g. "Hasta till 19:19," → "then Chitra"). */}
                {tail.length > 0 && <span className="text-[12.5px]" style={{ color: 'var(--kg-sub)' }}>,</span>}
                {accessory && <span className="ml-2.5 inline-flex items-center gap-1.5 align-middle">{accessory}</span>}
              </span>
              {badgesFor && first !== undefined && <span className="flex flex-wrap items-center gap-1">{badgesFor(first)}</span>}
            </div>
          )}

          {/* Lone subsequent segment — its own row, badges preserved. */}
          {!mergeTail && tail.map((line, i) => {
            const { timing } = line;
            return (
              <div key={i} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span className="min-w-0">
                  <span className="text-[13px]" style={{ color: 'var(--kg-sub)' }}>{L('then')}{followName(line)}</span>
                  {timingSpan(timing)}
                </span>
                {badgesFor && <span className="flex flex-wrap items-center gap-1">{badgesFor(line)}</span>}
              </div>
            );
          })}

          {/* Longer tail — one flowing "then A till x, then B" line. Each segment
              keeps its own "then" so the comma reads as a sequence, not a list. */}
          {mergeTail && (
            <div className="text-[13px] leading-snug" style={{ color: 'var(--kg-sub)' }}>
              {tail.map((line, i) => {
                const t = line.timing;
                // English: "then A till x, then B". Elsewhere the server's connectives,
                // "ఆపై A x వరకు మరియు B తర్వాత", so the line reads as a sentence.
                const lead = locale === 'en' ? L('then') : `${i === 0 ? L('thenWord') : L('andWord')} `;
                const sep = locale === 'en' ? ', ' : ' ';
                return (
                  <React.Fragment key={i}>
                    {i > 0 && sep}
                    {lead}<span>{followName(line)}</span>
                    {t && timingSpan(t)}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const sectionStyle: React.CSSProperties = {
    background: 'var(--kg-card)',
    border: '1px solid var(--kg-line)',
    boxShadow: 'var(--kg-shadow)',
  };

  return (
    <div className="kg-warm space-y-4" style={{ color: 'var(--kg-ink)' }}>
      {/* ── Context band ─────────────────────────────────────────────── */}
      {bandCells.length > 0 && (
        <section className="overflow-hidden rounded-2xl" style={sectionStyle}>
          <div className="grid grid-cols-2 gap-px sm:grid-cols-3 lg:grid-cols-5" style={{ background: 'var(--kg-line)' }}>
            {bandCells.map(cell => (
              <div key={cell.label} className="p-4" style={{ background: 'var(--kg-card)' }}>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--kg-faint)' }}>
                  {cell.label}
                </div>
                {'masa' in cell ? (
                  // Both frames stacked identically: name, then its frame label below.
                  <div className="mt-1.5 space-y-2">
                    <div>
                      <div className="font-fraunces text-[19px] font-medium leading-tight" style={{ color: 'var(--kg-ink)' }}>{cell.masa.amaanta}</div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--kg-faint)' }}>{L('amanta')}</div>
                    </div>
                    {cell.masa.purnimaanta && (
                      <div>
                        <div className="font-fraunces text-[19px] font-medium leading-tight" style={{ color: 'var(--kg-ink)' }}>{cell.masa.purnimaanta}</div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--kg-faint)' }}>{L('purnimanta')}</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="font-fraunces mt-1.5 flex items-center gap-2 text-[22px] font-medium leading-tight" style={{ color: 'var(--kg-ink)' }}>
                    {cell.icon}
                    <span>{cell.value}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Sun/moon times (below band, above the limbs) ─────────────── */}
      {panchanga.sunrise && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-1 text-[13px]" style={{ color: 'var(--kg-sub)' }}>
          <span className="flex items-center gap-1.5">
            <Sunrise className="h-4 w-4" style={{ color: 'var(--kg-sub)' }} aria-hidden="true" /> {L('sunrise')} {asciiDigits(panchanga.sunrise || '--')}
          </span>
          <span className="flex items-center gap-1.5">
            <Sunset className="h-4 w-4" style={{ color: 'var(--kg-sub)' }} aria-hidden="true" /> {L('sunset')} {asciiDigits(panchanga.sunset || '--')}
          </span>
          <span className="flex items-center gap-1.5">
            <Moon className="h-4 w-4" style={{ color: 'var(--kg-sub)' }} aria-hidden="true" /> {L('moonrise')} {asciiDigits(panchanga.moonrise || '--')}
          </span>
        </div>
      )}

      {/* ── Five-aṅga table ──────────────────────────────────────────── */}
      {hasLimbs && (
        <section className="rounded-2xl px-5 py-2 sm:px-7" style={sectionStyle}>
          <div className="divide-y" style={{ borderColor: 'var(--kg-line)' }}>
            {tithiSegments.length > 0 && renderLimb(
              'tithi', <SanskritTerm term={L('tithi')} explanation={SANSKRIT_EXPLANATIONS.tithi} />,
              tithiSegments, tithiBadges, undefined,
              moonIllum != null ? (
                <>
                  <MoonPhase
                    illumination={moonIllum}
                    waxing={isWaxingMoon}
                    size={17}
                    litColor={MOON_LIT}
                    darkColor={MOON_DARK}
                    strokeColor={MOON_STROKE}
                  />
                  <span className="text-[12px]" style={{ color: 'var(--kg-sub)' }}>{Math.round(moonIllum)}% {L('lit')}</span>
                </>
              ) : undefined,
            )}
            {panchanga.vaara && panchanga.vaara !== '--' && renderLimb(
              'vaara', <SanskritTerm term={L('vara')} explanation={SANSKRIT_EXPLANATIONS.vaara} />,
              [{ name: nfc(panchanga.vaara), timing: null, open: false, number: null, raw: panchanga.vaara }],
              () => vaaraBadges(panchanga.vaara_en || panchanga.vaara), L('allDay'),
            )}
            {nakshatraSegments.length > 0 && renderLimb(
              'nakshatra', <SanskritTerm term={L('nakshatra')} explanation={SANSKRIT_EXPLANATIONS.nakshatra} />,
              nakshatraSegments, nakshatraBadges,
            )}
            {yogaSegments.length > 0 && renderLimb(
              'yoga', <SanskritTerm term={L('yoga')} explanation={SANSKRIT_EXPLANATIONS.yoga} />,
              yogaSegments,
            )}
            {karanaSegments.length > 0 && renderLimb(
              'karana', <SanskritTerm term={L('karana')} explanation={SANSKRIT_EXPLANATIONS.karana} />,
              karanaSegments,
            )}
          </div>

          {nakshatraFooterLines.length > 0 && (
            <div className="space-y-2.5 py-3" style={{ borderTop: '1px solid var(--kg-line)' }}>
              {nakshatraFooterLines.map((line, i) => (
                <div key={i}>
                  <div className="flex flex-wrap items-baseline gap-x-2.5">
                    <span className="font-fraunces text-[14px] italic" style={{ color: 'var(--kg-ink)' }}>
                      {line.name}{line.standsFor ? ` · ${line.standsFor}` : ''}
                    </span>
                    {line.symbol && (
                      <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--kg-faint)' }}>
                        Symbol · {line.symbol}
                      </span>
                    )}
                  </div>
                  {line.desc && (
                    <div className="mt-0.5 text-[12px] leading-relaxed" style={{ color: 'var(--kg-sub)' }}>{line.desc}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Actions ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap justify-end gap-2 px-1">
        {onViewAlignment && (
          <button onClick={onViewAlignment} className="inline-flex items-center gap-1.5 rounded-[9px] px-3 py-1.5 text-sm transition-colors kg-pill">
            <ExternalLink className="h-3.5 w-3.5" /> {L('alignmentChart')}
          </button>
        )}
        {typeof navigator.share === 'function' && (
          <button
            onClick={() => {
              navigator.share({
                title: `Panchanga - ${panchanga.date}`,
                text: `Tithi: ${panchanga.tithi || ''}, Nakshatra: ${panchanga.nakshatra || ''}, Yoga: ${panchanga.yoga || ''}`,
                url: window.location.href,
              }).catch(() => {});
            }}
            className="inline-flex items-center gap-1.5 rounded-[9px] px-3 py-1.5 text-sm transition-colors kg-pill"
          >
            <Share2 className="h-3.5 w-3.5" /> {L('share')}
          </button>
        )}
        <button
          onClick={() => {
            const text = `Panchanga for ${panchanga.date}\nTithi: ${panchanga.tithi || '-'}\nNakshatra: ${panchanga.nakshatra || '-'}\nYoga: ${panchanga.yoga || '-'}\nKarana: ${panchanga.karana || '-'}${panchanga.festivals?.length ? '\nFestivals: ' + panchanga.festivals.join(', ') : ''}`;
            navigator.clipboard.writeText(text).catch(() => {});
          }}
          className="inline-flex items-center gap-1.5 rounded-[9px] px-3 py-1.5 text-sm transition-colors kg-pill"
        >
          <Copy className="h-3.5 w-3.5" /> {L('copy')}
        </button>
      </div>
    </div>
  );
};

export default PanchangaCard;
